import { 
  saveControl,
  updateControlEvaluationField
} from '../../redux/actions/controlActions';

export class ActionPlanningService {
  constructor(dispatch, modelId, assessmentUuid, liveUpdateCallback = null) {
    this.dispatch = dispatch;
    this.modelId = modelId;
    this.assessmentUuid = assessmentUuid;
    this.liveUpdateCallback = liveUpdateCallback;
  }

  async executeActionPlanning(areaName, riskFormData, riskModel, contextData, progressCallback) {
    try {
      console.log(`[ActionPlanningService] Starting action planning for area: ${areaName}`);
      if (progressCallback) progressCallback('actions');
      await new Promise(resolve => setTimeout(resolve, 600)); 

      // Get fresh Redux state instead of stale riskModel parameter
      const getState = () => this.dispatch((dispatch, getState) => getState());
      const currentState = getState();
      const freshRiskModel = currentState.risks.models[this.modelId] || {};
      const areaRisks = freshRiskModel.riskFormData[areaName]?.sectionRisks || {};
      const evaluatedRisks = [];
      
      console.log(`[ActionPlanningService] 🔍 Searching for evaluated risks in area: ${areaName}`);
      console.log(`[ActionPlanningService] Available risk sources:`, Object.keys(areaRisks));
      
      Object.entries(areaRisks).forEach(([sourceName, riskArray]) => {
        console.log(`[ActionPlanningService] Checking source "${sourceName}": ${Array.isArray(riskArray) ? riskArray.length : 'not array'} risks`);
        if (Array.isArray(riskArray)) {
          riskArray.forEach((risk, idx) => {
            console.log(`[ActionPlanningService] Risk ${idx}: "${risk.riskName}" - status: ${risk.status}, priority: ${risk.priority}`);
            if (risk.status === 'Saved' && risk.priority) {
              console.log(`[ActionPlanningService] ✅ FOUND evaluated risk for action planning: "${risk.riskName}"`);
              evaluatedRisks.push({
                ...risk,
                areaName,
                sourceName
              });
            }
          });
        }
      });

      let actionsGenerated = 0;

      // Double-check: filter out any risks not from current area
      const currentAreaRisks = evaluatedRisks.filter(risk => {
        if (risk.areaName !== areaName) {
          console.log(`[ActionPlanningService] ⚠️ SKIPPING risk from wrong area: "${risk.riskName}" (area: ${risk.areaName}, expected: ${areaName})`);
          return false;
        }
        return true;
      });
      
      console.log(`[ActionPlanningService] Processing ${currentAreaRisks.length} risks from current area (filtered from ${evaluatedRisks.length} total)`);

      // Generate and evaluate actions per risk using existing backend
      for (const risk of currentAreaRisks) {
        try {
          // Generate actions using existing function
          const controlsResponse = await fetch('/.netlify/functions/generateActionsByRisk', {
            method: 'POST', 
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({
              sectionName: areaName,
              riskSourceData: { name: risk.sourceName, causes: [] },
              riskName: risk.riskName,
              riskId: risk.riskId,
              narratives: contextData,
              controlModelType: 'advanced',
              controlModelStage: 'production'
            })
          });

          if (controlsResponse.ok) {
            const controls = await controlsResponse.json();
            
            // Auto-select top 3 controls and save them
            const topControls = controls.slice(0, 3);
            
            // LIVE UPDATE: Stream control generation for this risk
            if (this.liveUpdateCallback) {
              this.liveUpdateCallback('controlsGenerated', {
                riskName: risk.riskName,
                controlsCount: topControls.length,
                actionsGenerated
              });
            }
            
            for (const control of topControls) {
              // Save control using existing Redux action
              this.dispatch(saveControl({
                risk,
                control,
                modelId: this.modelId,
                assessmentUuid: this.assessmentUuid,
                controlModelType: 'advanced', 
                controlModelStage: 'production'
              }));

              // Generate evaluation for the control
              const controlEvalCfg = riskModel?.controlEvaluationForm || [];
              if (controlEvalCfg.length > 0) {
                const evalResponse = await fetch('/.netlify/functions/generateControlEvaluationData', {
                  method: 'POST',
                  headers: { 'Content-Type': 'application/json' },
                  body: JSON.stringify({
                    risk,
                    control,
                    evaluationConfig: controlEvalCfg
                  })
                });

                if (evalResponse.ok) {
                  const evalData = await evalResponse.json();
                  if (evalData.success && evalData.evaluationData) {
                    // Save evaluation using existing Redux action
                    this.dispatch(updateControlEvaluationField(
                      risk.riskId, 
                      control.controlId, 
                      'generated_evaluation', 
                      evalData.evaluationData
                    ));
                    
                    // LIVE UPDATE: Stream control evaluation as it completes
                    if (this.liveUpdateCallback) {
                      this.liveUpdateCallback('controlEvaluated', {
                        riskName: risk.riskName,
                        controlName: control.name || control.title,
                        evaluationData: evalData.evaluationData
                      });
                    }
                  }
                }
              }
              
              actionsGenerated++;
            }
          }
        } catch (actionError) {
          console.error(`[ActionPlanningService] Action planning failed for ${risk.riskName}:`, actionError);
        }
      }

      return {
        evaluatedRisks: evaluatedRisks.length,
        actionsGenerated,
        areaName
      };

    } catch (error) {
      console.error('[ActionPlanningService] Action planning failed:', error);
      throw error;
    }
  }
}